import type {
	CustomerQuoteBoardItem,
	QuoteBoardItem,
} from "~/shared/schemas/quote-board-schema.ts";

import type { QuoteBoardStatusContext } from "./quote-board-status.ts";
import { canTenderQuoteBoardRow } from "./quote-board-status.ts";

type BulkTenderBoardItem = QuoteBoardItem | CustomerQuoteBoardItem;
type CarrierId = number | null | undefined;

export const MAX_BULK_TENDER_SHIPMENTS = 500;

export const getBulkTenderLimitError = (
	quoteCount: number,
): string | undefined => {
	if (quoteCount <= MAX_BULK_TENDER_SHIPMENTS) return undefined;

	const excessCount = quoteCount - MAX_BULK_TENDER_SHIPMENTS;
	return `Bulk tendering supports up to ${String(MAX_BULK_TENDER_SHIPMENTS)} shipments at a time. Remove ${String(excessCount)} shipment${excessCount === 1 ? "" : "s"} and try again.`;
};

export type BulkTenderPreflightResult =
	| {
			success: true;
			quoteIds: number[];
	  }
	| {
			success: false;
			error: string;
			invalidQuoteIds: number[];
	  };

type CarrierIdResolver = (item: BulkTenderBoardItem) => CarrierId;

const normalizeQuoteIds = (quoteIds: readonly number[]): number[] =>
	[...new Set(quoteIds)].sort((left, right) => left - right);

const formatQuoteIds = (quoteIds: readonly number[]): string =>
	quoteIds.length > 0 ? ` (${quoteIds.join(", ")})` : "";

export const validateBulkTenderSelection = (
	items: readonly BulkTenderBoardItem[],
	quoteIds: readonly number[],
	getCarrierId?: CarrierIdResolver,
	context?: QuoteBoardStatusContext,
): BulkTenderPreflightResult => {
	const normalizedQuoteIds = normalizeQuoteIds(quoteIds);
	if (normalizedQuoteIds.length === 0) {
		return {
			success: false,
			error: "Select at least one valid shipment before tendering.",
			invalidQuoteIds: [],
		};
	}
	const limitError = getBulkTenderLimitError(normalizedQuoteIds.length);
	if (limitError) {
		return {
			success: false,
			error: limitError,
			invalidQuoteIds: normalizedQuoteIds,
		};
	}

	const itemsByQuoteId = new Map(
		items.map((item) => [item.quoteId, item] as const),
	);
	const missingQuoteIds = normalizedQuoteIds.filter(
		(quoteId) => !itemsByQuoteId.has(quoteId),
	);
	if (missingQuoteIds.length > 0) {
		return {
			success: false,
			error: `Refresh the Quote Board before tendering the selected shipments${formatQuoteIds(missingQuoteIds)}.`,
			invalidQuoteIds: missingQuoteIds,
		};
	}

	const selectedItems = normalizedQuoteIds
		.map((quoteId) => itemsByQuoteId.get(quoteId))
		.filter((item): item is BulkTenderBoardItem => item != null);
	const ineligibleQuoteIds = selectedItems
		.filter((item) => !canTenderQuoteBoardRow(item, context))
		.map((item) => item.quoteId);
	if (ineligibleQuoteIds.length > 0) {
		return {
			success: false,
			error: `Only Ready to Tender shipments can be submitted${formatQuoteIds(ineligibleQuoteIds)}. Refresh and try again.`,
			invalidQuoteIds: ineligibleQuoteIds,
		};
	}

	if (getCarrierId) {
		const carrierlessQuoteIds = selectedItems
			.filter((item) => getCarrierId(item) == null)
			.map((item) => item.quoteId);
		if (carrierlessQuoteIds.length > 0) {
			return {
				success: false,
				error: `Select a carrier for every selected shipment before tendering${formatQuoteIds(carrierlessQuoteIds)}.`,
				invalidQuoteIds: carrierlessQuoteIds,
			};
		}
	}

	return { success: true, quoteIds: normalizedQuoteIds };
};
